import {
	AddOnsPriceEntity,
	AdditionalBookingOptionEntity,
	DefaultPricingEntity,
	FreightPriceRangeEntity,
	MeasurementEntity,
	PassengerTypeEntity,
	PriceEntity,
	RouteEntity,
	UserEntity,
} from 'Models/Entities';
import { PricingStrategyIds, PricingStrategys } from 'Global/Pricing/PricingStrategy';
import { PriceCellState, cellsEqual, describeCell, readCell } from 'Global/Pricing/PriceCellState';
import { UserPricingFormData } from 'Services/Api/_HumanWritten/UserFixedPricingService';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';

/**
 * Plain-object rows backing the account page's Pricing tab. The shared pricing tables bind
 * MobX PriceEntity models directly; the user-account page instead keeps its pricing in
 * react-hook-form state as these rows, edited immutably, and only constructs entities at the
 * save boundary (rowToPriceEntity / rowToAddOnEntity). Display metadata (labels, measurement
 * values) is flattened in so rendering never needs the linked entities.
 */

export interface UserPriceRowMeasurement {
	id?: string;
	label?: string;
	value?: number;
	measurementType?: string;
}

export interface UserPriceRowFreightRange {
	id?: string;
	freightCategoryId?: string;
	freightCategoryLabel?: string;
	freightHandlingId?: string | null;
	freightHandlingLabel?: string;
	maxValue?: number;
	isFixedPrice?: boolean;
}

export interface UserPriceRow {
	id?: string;
	amount?: number | null;
	mode: 'FIXED' | 'PERCENTAGE';
	passengerTypeId?: string;
	measurementId?: string;
	measurement?: UserPriceRowMeasurement;
	trailerMeasurementId?: string;
	trailerMeasurement?: UserPriceRowMeasurement;
	freightPriceRangeId?: string;
	freightPriceRange?: UserPriceRowFreightRange;
	freightTypeId?: string;
	routeId?: string;
	startStopId?: string;
	endStopId?: string;
	pricingStrategyId?: string;
	/**
	 * UI-only: a blank Fixed cell is indistinguishable from Standard on the data alone
	 * (amount=null, mode=FIXED), so an explicit "Fixed" choice is recorded here until an
	 * amount lands. Replaces the shared tables' pendingFixedRegistry. Never saved.
	 */
	pendingFixed?: boolean;
}

export interface UserAddOnPriceRow {
	id?: string;
	price?: number | null;
	additionalBookingOptionId?: string;
	additionalBookingOptionName?: string;
}

export type PricingStrategyKind = keyof typeof PricingStrategys;

// ---------------------------------------------------------------------------
// Entity ⇄ row mapping (the only places entities appear)
// ---------------------------------------------------------------------------

function measurementToRow(m?: MeasurementEntity | null): UserPriceRowMeasurement | undefined {
	if (isNullOrUndefined(m)) return undefined;
	return {
		id: m.id,
		label: m.label,
		value: m.value,
		measurementType: m.measurementType,
	};
}

function freightRangeToRow(f?: FreightPriceRangeEntity | null): UserPriceRowFreightRange | undefined {
	if (isNullOrUndefined(f)) return undefined;
	return {
		id: f.id,
		freightCategoryId: f.freightCategoryId,
		freightCategoryLabel: f.freightCategory?.label,
		freightHandlingId: f.freightHandlingId,
		freightHandlingLabel: f.freightHandling?.label,
		maxValue: f.maxValue,
		isFixedPrice: f.isFixedPrice,
	};
}

export function priceEntityToRow(p: PriceEntity): UserPriceRow {
	return {
		id: p.id,
		amount: p.amount ?? null,
		mode: p.mode === 'PERCENTAGE' ? 'PERCENTAGE' : 'FIXED',
		passengerTypeId: p.passengerTypeId ?? undefined,
		measurementId: p.measurementId ?? undefined,
		measurement: measurementToRow(p.measurement),
		trailerMeasurementId: p.trailerMeasurementId ?? undefined,
		trailerMeasurement: measurementToRow(p.trailerMeasurement),
		freightPriceRangeId: p.freightPriceRangeId ?? undefined,
		freightPriceRange: freightRangeToRow(p.freightPriceRange),
		freightTypeId: p.freightTypeId ?? undefined,
		routeId: p.routeId ?? undefined,
		startStopId: p.startStopId ?? undefined,
		endStopId: p.endStopId ?? undefined,
		pricingStrategyId: p.pricingStrategyId ?? undefined,
	};
}

export function addOnEntityToRow(a: AddOnsPriceEntity): UserAddOnPriceRow {
	return {
		id: a.id,
		price: a.price ?? null,
		additionalBookingOptionId: a.additionalBookingOptionId ?? a.additionalBookingOption?.id,
		additionalBookingOptionName: a.additionalBookingOption?.name,
	};
}

/** Save boundary: ids + amount/mode are what the save payload serialises (children aren't included). */
export function rowToPriceEntity(row: UserPriceRow): PriceEntity {
	return new PriceEntity({
		id: row.id,
		amount: row.amount ?? undefined,
		mode: row.mode,
		passengerTypeId: row.passengerTypeId,
		measurementId: row.measurementId,
		trailerMeasurementId: row.trailerMeasurementId,
		freightPriceRangeId: row.freightPriceRangeId,
		freightTypeId: row.freightTypeId,
		routeId: row.routeId,
		startStopId: row.startStopId,
		endStopId: row.endStopId,
		pricingStrategyId: row.pricingStrategyId,
	});
}

export function rowToAddOnEntity(row: UserAddOnPriceRow): AddOnsPriceEntity {
	return new AddOnsPriceEntity({
		id: row.id,
		price: row.price ?? undefined,
		additionalBookingOptionId: row.additionalBookingOptionId,
	});
}

/** New rows carrying a type's linked default pricing (no ids — they're fresh, unsaved rows). */
export function defaultPricingToRows(defaultPricing: DefaultPricingEntity): {
	prices: UserPriceRow[];
	addOnsPrices: UserAddOnPriceRow[];
} {
	return {
		prices: (defaultPricing.prices ?? []).map(
			x =>
				({
					amount: x.amount ?? null,
					mode: x.mode === 'PERCENTAGE' ? 'PERCENTAGE' : 'FIXED',
					measurementId: x.measurementId ?? undefined,
					measurement: measurementToRow(x.measurement),
					trailerMeasurementId: x.trailerMeasurementId ?? undefined,
					trailerMeasurement: measurementToRow(x.trailerMeasurement),
					passengerTypeId: x.passengerTypeId ?? undefined,
					freightPriceRangeId: x.freightPriceRangeId ?? undefined,
					freightPriceRange: freightRangeToRow(x.freightPriceRange),
					freightTypeId: x.freightTypeId ?? undefined,
					routeId: x.routeId ?? undefined,
					startStopId: x.startStopId ?? undefined,
					endStopId: x.endStopId ?? undefined,
					pricingStrategyId: x.pricingStrategyId ?? undefined,
				}) as UserPriceRow,
		),
		addOnsPrices: (defaultPricing.addOnsPrices ?? []).map(
			x =>
				({
					price: x.price ?? null,
					additionalBookingOptionId: x.additionalBookingOptionId ?? x.additionalBookingOption?.id,
					additionalBookingOptionName: x.additionalBookingOption?.name,
				}) as UserAddOnPriceRow,
		),
	};
}

export function mapUserPricingToRows(user: UserEntity): {
	prices: UserPriceRow[];
	addOnsPrices: UserAddOnPriceRow[];
} {
	return {
		prices: (user.prices ?? []).map(priceEntityToRow),
		addOnsPrices: (user.addOnsPrices ?? []).map(addOnEntityToRow),
	};
}

// ---------------------------------------------------------------------------
// Cell state over rows (mirrors Global/Pricing/PriceCellState for entities)
// ---------------------------------------------------------------------------

/** Row counterpart of readCell — pendingFixed is deliberately ignored (a blank Fixed cell is Standard on data). */
export function readRowCell(row: Pick<UserPriceRow, 'amount' | 'mode'>): PriceCellState {
	if (row.mode === 'PERCENTAGE') {
		return { kind: 'PERCENTAGE', amount: row.amount ?? undefined };
	}
	if (row.amount == null) {
		return { kind: 'INHERIT' };
	}
	return { kind: 'FIXED', amount: row.amount };
}

/** Immutable counterpart of writeCell: returns a new row carrying the cell state. */
export function applyCellToRow(row: UserPriceRow, next: PriceCellState): UserPriceRow {
	switch (next.kind) {
		case 'INHERIT':
			return {
				...row,
				amount: null,
				mode: 'FIXED',
				pendingFixed: false,
			};
		case 'FIXED':
			// A blank Fixed write records the explicit choice via pendingFixed (see the field doc).
			return {
				...row,
				amount: next.amount ?? null,
				mode: 'FIXED',
				pendingFixed: next.amount == null,
			};
		case 'PERCENTAGE':
			return {
				...row,
				amount: next.amount ?? null,
				mode: 'PERCENTAGE',
				pendingFixed: false,
			};
		default: {
			const _exhaustive: never = next;
			throw new Error(`Unhandled PriceCellState kind: ${JSON.stringify(_exhaustive)}`);
		}
	}
}

/**
 * One logical pricing slot = route + dimension (+ per-segment stop). The seed pass dedupes on
 * this key, so it uniquely identifies a row; cell writes address rows by it rather than by
 * object identity (react-hook-form may clone values).
 */
export function priceRowSlotKey(row: UserPriceRow): string {
	return (
		`${row.routeId ?? 'd'}|${row.measurementId ?? ''}|${row.trailerMeasurementId ?? ''}` +
		`|${row.passengerTypeId ?? ''}|${row.freightPriceRangeId ?? ''}|${row.startStopId ?? ''}`
	);
}

/**
 * Write a cell state into the row occupying the target's slot, appending the target as a new
 * row when the slot doesn't exist yet — tables render virtual placeholder rows for missing
 * slots (the legacy tables pushed them into the observable array mid-render), and the row only
 * materialises on first edit.
 */
export function upsertPriceRow(rows: UserPriceRow[], target: UserPriceRow, next: PriceCellState): UserPriceRow[] {
	const key = priceRowSlotKey(target);
	let found = false;
	const result = rows.map(row => {
		if (priceRowSlotKey(row) === key) {
			found = true;
			return applyCellToRow(row, next);
		}
		return row;
	});
	if (!found) {
		result.push(applyCellToRow(target, next));
	}
	return result;
}

// ---------------------------------------------------------------------------
// Derivations (the UserEntity hasXPricing methods, over rows)
// ---------------------------------------------------------------------------

export interface PricingSections {
	passenger: boolean;
	vehicleTrailer: boolean;
	trailer: boolean;
	addOns: boolean;
	freightCategory: boolean;
	freightHandling: boolean;
}

export function derivePricingSections(rows: UserPriceRow[], addOns: UserAddOnPriceRow[]): PricingSections {
	const priced = (predicate: (row: UserPriceRow) => boolean) =>
		rows.filter(predicate).some(row => isNotNullOrUndefined(row.amount));
	return {
		passenger: priced(row => isNotNullOrUndefined(row.passengerTypeId)),
		vehicleTrailer: priced(row => isNotNullOrUndefined(row.measurementId)),
		trailer: priced(row => isNotNullOrUndefined(row.trailerMeasurementId)),
		addOns: addOns.some(a => isNotNullOrUndefined(a.price)),
		// Both freight sections key off category pricing — mirrors the legacy selection reaction.
		freightCategory: priced(row => isNotNullOrUndefined(row.freightPriceRange?.freightCategoryId)),
		freightHandling: priced(row => isNotNullOrUndefined(row.freightPriceRange?.freightCategoryId)),
	};
}

export type ClearablePriceSection = 'passenger' | 'vehicleTrailer' | 'trailer' | 'freightCategory';

const SECTION_PREDICATES: { [K in ClearablePriceSection]: (row: UserPriceRow) => boolean } = {
	passenger: row => isNotNullOrUndefined(row.passengerTypeId),
	vehicleTrailer: row => isNotNullOrUndefined(row.measurementId),
	trailer: row => isNotNullOrUndefined(row.trailerMeasurementId),
	freightCategory: row => isNotNullOrUndefined(row.freightPriceRangeId),
};

/**
 * Unchecking a section drops the amount off each of its rows while keeping the placeholder
 * slot (route/strategy/id) intact — the same shape the legacy clear handlers produced.
 */
export function clearSectionRows(rows: UserPriceRow[], section: ClearablePriceSection): UserPriceRow[] {
	const matches = SECTION_PREDICATES[section];
	return rows.map(row =>
		matches(row)
			? {
					...row,
					amount: null,
					mode: 'FIXED',
					pendingFixed: false,
				}
			: row,
	);
}

export function clearAddOnRows(rows: UserAddOnPriceRow[]): UserAddOnPriceRow[] {
	return rows.map(row => ({ ...row, price: null }));
}

// ---------------------------------------------------------------------------
// Seeding (pure port of the legacy placeholder-seeding effect)
// ---------------------------------------------------------------------------

export interface SeedContext {
	formData: UserPricingFormData;
	routes: RouteEntity[];
	pricingStrategy: PricingStrategyKind;
	vehicleEnabled: boolean;
	trailerHasOwnPricing: boolean;
	freightEnabled: boolean;
}

const measurementFromFormData = (m: MeasurementEntity): UserPriceRowMeasurement => ({
	id: m.id,
	label: m.label,
	value: m.value,
	measurementType: m.measurementType,
});

/**
 * Seed placeholder rows for every (route × measurement / freight range) combination that's
 * missing, so the pricing tables show a row per route even when the user has no saved price
 * for it. Then collapse duplicate rows sharing a logical slot (duplicates arise from historical
 * saves that orphaned rows server-side, and from default-pricing rows being re-copied across
 * user changes — the tables render the first row of each slot, so duplicates make typed values
 * "flip" between renders; prefer the row with an amount) and stable-sort so slot order is
 * consistent across renders.
 */
export function seedPriceRows(existing: UserPriceRow[], ctx: SeedContext): UserPriceRow[] {
	const { formData, routes, pricingStrategy, vehicleEnabled, trailerHasOwnPricing, freightEnabled } = ctx;
	const rows = existing.slice();
	const pricingStrategyId = PricingStrategyIds[pricingStrategy];

	const matchesRoute = (row: UserPriceRow, route: RouteEntity) =>
		(isNullOrUndefined(route.id) && isNullOrUndefined(row.routeId)) || route.id === row.routeId;

	const pushIfMissing = (exists: (row: UserPriceRow) => boolean, build: () => UserPriceRow) => {
		if (!rows.some(exists)) rows.push(build());
	};

	if (vehicleEnabled && formData.measurementTypes?.length) {
		routes.forEach(route => {
			const seedMeasurement = (m: MeasurementEntity, startStopId?: string) => {
				pushIfMissing(
					row =>
						row.measurementId === m.id &&
						matchesRoute(row, route) &&
						(startStopId === undefined || row.startStopId === startStopId),
					() => ({
						measurementId: m.id,
						routeId: route.id,
						startStopId,
						amount: null,
						mode: 'FIXED',
						pricingStrategyId,
						measurement: measurementFromFormData(m),
					}),
				);
				if (trailerHasOwnPricing) {
					pushIfMissing(
						row =>
							row.trailerMeasurementId === m.id &&
							matchesRoute(row, route) &&
							(startStopId === undefined || row.startStopId === startStopId),
						() => ({
							trailerMeasurementId: m.id,
							routeId: route.id,
							startStopId,
							amount: null,
							mode: 'FIXED',
							pricingStrategyId,
							trailerMeasurement: measurementFromFormData(m),
						}),
					);
				}
			};

			if (pricingStrategy === 'ConsecutiveStops') {
				route.segments.forEach(segment => {
					formData.measurementTypes.forEach(m => seedMeasurement(m, segment.startStop.id));
				});
			} else {
				formData.measurementTypes.forEach(m => seedMeasurement(m));
			}
		});
	}

	if (freightEnabled && formData.freightPriceRanges?.length) {
		routes.forEach(route => {
			const seedFreight = (f: FreightPriceRangeEntity, startStopId?: string) => {
				pushIfMissing(
					row =>
						row.freightPriceRangeId === f.id &&
						matchesRoute(row, route) &&
						(startStopId === undefined || row.startStopId === startStopId),
					() => ({
						freightPriceRangeId: f.id,
						routeId: route.id,
						startStopId,
						amount: null,
						mode: 'FIXED',
						pricingStrategyId,
						freightPriceRange: freightRangeToRow(f),
					}),
				);
			};

			if (pricingStrategy === 'ConsecutiveStops') {
				route.segments.forEach(segment => {
					formData.freightPriceRanges.forEach(f => seedFreight(f, segment.startStop.id));
				});
			} else {
				formData.freightPriceRanges.forEach(f => seedFreight(f));
			}
		});
	}

	// Collapse duplicate rows per logical slot, preferring the row with an amount set.
	const bySlot = new Map<string, UserPriceRow>();
	rows.forEach(row => {
		const key = priceRowSlotKey(row);
		const current = bySlot.get(key);
		if (!current) {
			bySlot.set(key, row);
			return;
		}
		if (isNotNullOrUndefined(row.amount) && isNullOrUndefined(current.amount)) {
			bySlot.set(key, row);
		}
	});

	// Stable sort (equal keys compare 0) so each slot's position is consistent across renders.
	const label = (row: UserPriceRow) =>
		row.trailerMeasurement?.value ?? row.measurement?.value ?? row.freightPriceRange?.freightCategoryLabel ?? '';
	return Array.from(bySlot.values()).sort((a, b) => {
		const aLabel = label(a);
		const bLabel = label(b);
		if (aLabel < bLabel) return -1;
		if (aLabel > bLabel) return 1;
		return 0;
	});
}

/** Add a placeholder add-on row for every booking option the user doesn't have one for yet. */
export function seedAddOnRows(
	existing: UserAddOnPriceRow[],
	options: AdditionalBookingOptionEntity[],
): UserAddOnPriceRow[] {
	const rows = existing.slice();
	(options ?? []).forEach(option => {
		if (!rows.some(row => row.additionalBookingOptionId === option.id)) {
			rows.push({
				price: null,
				additionalBookingOptionId: option.id,
				additionalBookingOptionName: option.name,
			});
		}
	});
	return rows;
}

/**
 * Materialise every "All routes" placeholder row before a bulk apply runs, so "set all to
 * Standard/Percentage" covers every dimension — not just the ones the linked default happened
 * to price. Without this, a default that prices only some measurements leaves the rest with no
 * row at all, so the bulk write skips them and they never render.
 */
export function ensureAllRoutesRows(
	existing: UserPriceRow[],
	ctx: Omit<SeedContext, 'routes' | 'formData'> & {
		/** Nullable: passenger rows are still ensured while the metadata is loading (legacy parity). */
		formData: UserPricingFormData | null;
		passengerTypes: PassengerTypeEntity[];
	},
): UserPriceRow[] {
	const { formData, pricingStrategy, vehicleEnabled, trailerHasOwnPricing, freightEnabled, passengerTypes } = ctx;
	const rows = existing.slice();
	const pricingStrategyId = PricingStrategyIds[pricingStrategy];
	const hasAllRoutesRow = (predicate: (row: UserPriceRow) => boolean) =>
		rows.some(row => !row.routeId && !row.startStopId && predicate(row));

	passengerTypes.forEach(pt => {
		if (!hasAllRoutesRow(row => row.passengerTypeId === pt.id)) {
			rows.push({
				passengerTypeId: pt.id,
				amount: null,
				mode: 'FIXED',
				pricingStrategyId,
			});
		}
	});

	if (formData == null) return rows;

	if (vehicleEnabled && formData.measurementTypes?.length) {
		formData.measurementTypes.forEach(m => {
			if (!hasAllRoutesRow(row => row.measurementId === m.id)) {
				rows.push({
					measurementId: m.id,
					amount: null,
					mode: 'FIXED',
					pricingStrategyId,
					measurement: measurementFromFormData(m),
				});
			}
			if (trailerHasOwnPricing && !hasAllRoutesRow(row => row.trailerMeasurementId === m.id)) {
				rows.push({
					trailerMeasurementId: m.id,
					amount: null,
					mode: 'FIXED',
					pricingStrategyId,
					trailerMeasurement: measurementFromFormData(m),
				});
			}
		});
	}

	if (freightEnabled && formData.freightPriceRanges?.length) {
		formData.freightPriceRanges.forEach(f => {
			if (!hasAllRoutesRow(row => row.freightPriceRangeId === f.id)) {
				rows.push({
					freightPriceRangeId: f.id,
					amount: null,
					mode: 'FIXED',
					pricingStrategyId,
					freightPriceRange: freightRangeToRow(f),
				});
			}
		});
	}

	return rows;
}

// ---------------------------------------------------------------------------
// Sync-with-default comparison (pure port of the legacy isInSync banner logic)
// ---------------------------------------------------------------------------

/**
 * Whether the user's priced rows match the linked default pricing. Compares prices the default
 * pricing CRUD form manages (passenger / vehicle / trailer / freight), keyed by the category
 * foreign key + routeId ('all' for the every-route bucket) + startStopId. Rows with no amount
 * are skipped — those are placeholder cells for routes/measurements the default doesn't price.
 * Sync requires matching both the amount AND the mode — "10% off" and "$10" masking each other
 * would silently hide drift the pricing engine later resolves to completely different prices;
 * readCell()/readRowCell()/cellsEqual() own that rule.
 */
export function pricesInSyncWithDefault(
	rows: UserPriceRow[],
	defaultPricing: DefaultPricingEntity | null | undefined,
): boolean {
	if (!defaultPricing) return true;

	const keyOf = (p: {
		routeId?: string;
		startStopId?: string;
		passengerTypeId?: string;
		measurementId?: string;
		trailerMeasurementId?: string;
		freightPriceRangeId?: string;
	}) => {
		const routePart = p?.routeId ?? 'all';
		const stopPart = p?.startStopId ?? '';
		if (p?.passengerTypeId) {
			return `passenger:${p.passengerTypeId}|route:${routePart}|stop:${stopPart}`;
		}
		if (p?.measurementId) {
			return `measurement:${p.measurementId}|route:${routePart}|stop:${stopPart}`;
		}
		if (p?.trailerMeasurementId) {
			return `trailer:${p.trailerMeasurementId}|route:${routePart}|stop:${stopPart}`;
		}
		if (p?.freightPriceRangeId) {
			return `freight:${p.freightPriceRangeId}|route:${routePart}|stop:${stopPart}`;
		}
		return null;
	};

	const defaultPriceMap = new Map<string, PriceCellState>();
	for (const dp of defaultPricing.prices ?? []) {
		const key = keyOf(dp);
		if (key && dp?.amount != null) {
			defaultPriceMap.set(key, readCell(dp));
		}
	}
	const userPriceMap = new Map<string, PriceCellState>();
	for (const row of rows ?? []) {
		const key = keyOf(row);
		if (key && row?.amount != null) {
			userPriceMap.set(key, readRowCell(row));
		}
	}

	const onlyInDefault: string[] = [];
	const onlyInUser: string[] = [];
	const differingValues: string[] = [];
	for (const [k, v] of defaultPriceMap) {
		if (!userPriceMap.has(k)) onlyInDefault.push(k);
		else if (!cellsEqual(userPriceMap.get(k)!, v)) {
			differingValues.push(`${k} (default=${describeCell(v)}, user=${describeCell(userPriceMap.get(k)!)})`);
		}
	}
	for (const k of userPriceMap.keys()) {
		if (!defaultPriceMap.has(k)) onlyInUser.push(k);
	}

	if (onlyInDefault.length || onlyInUser.length || differingValues.length) {
		// eslint-disable-next-line no-console
		console.debug('[default-pricing sync] out of sync', {
			onlyInDefault,
			onlyInUser,
			differingValues,
		});
		return false;
	}
	return true;
}
